// 产品资料与客户 / 供应商共用的数据钩子（只读，只走 api.get）。
// 与 orders/api.ts 的 useOrderList / useResource 同一写法，泛化了路径和 stats；建议主会话统一提取到 src/lib。
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { api } from '@/api/client';
import type { Page } from '@/api/types';

type Query = Record<string, string | number | undefined>;

type ListState<T, S> = {
  items: T[];
  total: number;
  page: number;
  hasNext: boolean;
  stats?: S;
  loading: boolean;
  error?: Error;
};

/**
 * 分页列表：切换条件时重置取第一页；append=true 时「加载更多」追加（手机 / iPad），否则翻页替换（桌面）。
 * 旧请求会被 abort，避免快速切换条件时旧结果覆盖新结果。
 */
export function usePagedList<T, S = unknown>(path: string, query: Query, pageSize: number, append: boolean, withStats = true) {
  const [state, setState] = useState<ListState<T, S>>({ items: [], total: 0, page: 1, hasNext: false, loading: true });
  const ctrl = useRef<AbortController | null>(null);
  const key = JSON.stringify(query);

  const fetchPage = useCallback(
    async (page: number, reset: boolean) => {
      ctrl.current?.abort();
      const c = new AbortController();
      ctrl.current = c;
      setState((s) => ({ ...s, loading: true, error: undefined }));
      try {
        const data = await api.get<Page<T> & { stats?: S }>(
          path,
          { ...(JSON.parse(key) as Query), page, pageSize, includeStats: withStats && page === 1 ? 1 : undefined },
          c.signal,
        );
        setState((s) => ({
          items: reset || !append ? data.items : [...s.items, ...data.items],
          total: data.pagination.total,
          page: data.pagination.page,
          hasNext: data.pagination.hasNext,
          stats: data.stats ?? s.stats,
          loading: false,
        }));
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        setState((s) => ({ ...s, loading: false, error: e as Error }));
      }
    },
    [path, key, pageSize, append, withStats],
  );

  useEffect(() => {
    fetchPage(1, true);
    return () => ctrl.current?.abort();
  }, [fetchPage]);
  useEffect(() => {
    const refresh = () => { void fetchPage(1, true); };
    window.addEventListener('ims:v2:refresh', refresh);
    return () => window.removeEventListener('ims:v2:refresh', refresh);
  }, [fetchPage]);

  return {
    ...state,
    pageSize,
    reload: () => fetchPage(1, true),
    loadMore: () => state.hasNext && !state.loading && fetchPage(state.page + 1, false),
    goto: (p: number) => fetchPage(p, true),
  };
}

export type PagedList<T, S = unknown> = ReturnType<typeof usePagedList<T, S>>;

/** 单个资源（详情）；path 为 null 时不请求 */
export function useResource<T>(path: string | null) {
  const [state, setState] = useState<{ data?: T; loading: boolean; error?: Error }>({ loading: !!path });
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    const refresh = () => setNonce((value) => value + 1);
    window.addEventListener('ims:v2:refresh', refresh);
    return () => window.removeEventListener('ims:v2:refresh', refresh);
  }, []);
  useEffect(() => {
    if (!path) return;
    const c = new AbortController();
    setState((s) => ({ ...s, loading: true, error: undefined }));
    api
      .get<T>(path, undefined, c.signal)
      .then((data) => setState({ data, loading: false }))
      .catch((e) => {
        if ((e as Error).name !== 'AbortError') setState((current) => ({ ...current, loading: false, error: e as Error }));
      });
    return () => c.abort();
  }, [path, nonce]);
  return { ...state, reload: () => setNonce((n) => n + 1) };
}

/** 视图 / 筛选 / 搜索 / 选中项都写进 URL 查询参数（replace，不堆历史） */
export function useUrlState() {
  const [sp, setSp] = useSearchParams();
  const set = useCallback(
    (patch: Record<string, string | undefined>) => {
      setSp(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (v) next.set(k, v);
            else next.delete(k);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSp],
  );
  return [sp, set] as const;
}

/** 手机 / iPad：滚到底自动加载下一页；count 变了重新观察（加载完一页后哨兵仍在视口里时继续取） */
export function useInfinite(el: HTMLElement | null, enabled: boolean, loadMore: () => unknown, count: number) {
  const fn = useRef(loadMore);
  fn.current = loadMore;
  useEffect(() => {
    if (!el || !enabled) return;
    const io = new IntersectionObserver((es) => es[0].isIntersecting && fn.current(), { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [el, enabled, count]);
}

/** iPad 分栏：选中项写进 ?id=，返回带 id 的链接 */
export function withParam(sp: URLSearchParams, key: string, value: string) {
  const n = new URLSearchParams(sp);
  n.set(key, value);
  return n.toString();
}
